import { AnimatePresence } from "framer-motion";
import { imgSrc } from "../../lib/api";
import { fmt } from "../../data/site";
import { PhotoUploader } from "../PhotoUploader";
import { ActivePill, EditorModal, inputCls } from "./EditorShell";
import { CardActions, EditorHeader, useAdminCollection } from "./useAdminCollection";

const EMPTY = { name: "", desc: "", price: 6, img: "", kind: "Side", cal: null, portion: "", active: true };
const KINDS = ["Side", "Dessert"];

export const ExtrasEditor = () => {
  const { items, editing, setEditing, busy, save, remove, toggleActive } = useAdminCollection("extras", "Add-on");
  const set = (k) => (e) => setEditing({ ...editing, [k]: e.target.value });

  const submit = (e) => {
    e.preventDefault();
    const { id, order, ...rest } = editing;
    save({ ...rest, price: Number(editing.price) });
  };

  return (
    <div data-testid="extras-editor">
      <EditorHeader
        summary={`${items.filter((i) => i.active).length} live add-ons · shown under "Sides & desserts" on the Meal Prep page.`}
        addLabel="Add Side / Dessert"
        testid="extras-add-btn"
        onAdd={() => setEditing({ ...EMPTY })}
      />
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {items.map((item) => (
          <div key={item.id} data-testid={`extras-item-${item.id.slice(0, 8)}`} className={`overflow-hidden rounded-2xl border border-inkline bg-parchment transition-all ${item.active ? "" : "opacity-55"}`}>
            <img src={imgSrc(item.img)} alt={item.name} className="h-32 w-full object-cover" />
            <div className="p-4">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-spice">{item.kind}</div>
              <div className="mt-1 flex items-start justify-between gap-2">
                <h4 className="font-display text-base font-semibold leading-snug text-ink">{item.name}</h4>
                <span className="shrink-0 font-mono text-sm font-bold text-spice">{fmt(item.price)}</span>
              </div>
              <p className="mt-1 line-clamp-2 text-xs text-muted2">{item.desc}</p>
              <div className="mt-3 flex items-center gap-2">
                <ActivePill active={item.active} onClick={() => toggleActive(item)} testid={`extras-active-${item.id.slice(0, 8)}`} />
                <CardActions item={item} prefix="extras" onEdit={setEditing} onDelete={remove} />
              </div>
            </div>
          </div>
        ))}
      </div>
      <AnimatePresence>
        {editing && (
          <EditorModal
            title={editing.id ? "Edit add-on" : "New side or dessert"}
            testid="extras-form"
            onClose={() => setEditing(null)}
            onSave={submit}
            busy={busy}
            saveLabel={editing.id ? "Save Changes" : "Add to Menu"}
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <input required data-testid="extras-form-name" placeholder="Name (e.g. Fattoush Salad)" value={editing.name} onChange={set("name")} className={inputCls} />
              <input required type="number" step="0.5" min="0.5" data-testid="extras-form-price" placeholder="Price ($)" value={editing.price} onChange={set("price")} className={inputCls} />
            </div>
            <textarea data-testid="extras-form-desc" rows={2} placeholder="Short description" value={editing.desc} onChange={set("desc")} className={inputCls} />
            <div className="grid gap-4 sm:grid-cols-2">
              <input data-testid="extras-form-portion" placeholder="Portion (e.g. 12 oz container)" value={editing.portion || ""} onChange={set("portion")} className={inputCls} />
              <input type="number" min="0" step="10" data-testid="extras-form-cal" placeholder="Calories (estimate)" value={editing.cal ?? ""} onChange={(e) => setEditing({ ...editing, cal: e.target.value === "" ? null : Number(e.target.value) })} className={inputCls} />
            </div>
            <div>
              <div className="mb-2 font-mono text-[10px] uppercase tracking-[0.2em] text-muted2">Type</div>
              <div className="flex gap-2">
                {KINDS.map((k) => (
                  <button key={k} type="button" data-testid={`extras-kind-${k.toLowerCase()}`} onClick={() => setEditing({ ...editing, kind: k })}
                    className={`rounded-full px-4 py-2 text-xs font-bold transition-colors ${editing.kind === k ? "bg-ink text-saffron" : "bg-cream text-ink/70 hover:bg-ink/10"}`}>
                    {k}
                  </button>
                ))}
              </div>
            </div>
            <PhotoUploader value={editing.img} onChange={(img) => setEditing((cur) => ({ ...cur, img }))} folder="extras" label="Photo" testid="extras-form-img" />
            <label className="flex items-center gap-3 text-sm font-medium text-ink">
              <input type="checkbox" data-testid="extras-form-active" checked={editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} className="h-4 w-4 accent-[#305830]" />
              Live on the Meal Prep page
            </label>
          </EditorModal>
        )}
      </AnimatePresence>
    </div>
  );
};
